/**
 * useUnifiedSync — single source of truth for cross-device synchronization.
 *
 * Works identically on desktop web, mobile web, Android, iOS and tablet.
 * Replaces useCacheSync + useMobileSync + useCacheInvalidation.
 *
 * Responsibilities:
 *  1. Boot a unified Realtime layer (`realtimeSync`) that invalidates
 *     React Query caches when any watched table changes.
 *  2. Invalidate all watched queries when the device comes back online,
 *     when the tab/app regains focus, or when a Capacitor app resumes.
 *  3. Broadcast invalidations between tabs of the same browser.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { startRealtimeSync, stopRealtimeSync, invalidateAll } from "@/lib/realtimeSync";

const BROADCAST_CHANNEL = "berastays_sync";

export function useUnifiedSync(userId: string | undefined) {
  const qc = useQueryClient();

  // 1) Realtime channels — restart when the user changes (auth state)
  useEffect(() => {
    startRealtimeSync(qc);
    return () => stopRealtimeSync();
  }, [qc, userId]);

  // 2) Window / tab focus + reconnect — invalidate, don't clear
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onFocus = () => invalidateAll(qc);
    const onOnline = () => invalidateAll(qc);
    const onVisibility = () => {
      if (document.visibilityState === "visible") invalidateAll(qc);
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [qc]);

  // 3) Native app resume (Capacitor)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle: { remove: () => void } | null = null;

    App.addListener("resume", () => invalidateAll(qc))
      .then((h) => {
        handle = h;
      })
      .catch(() => {
        /* ignore */
      });

    return () => {
      handle?.remove();
    };
  }, [qc]);

  // 4) Cross-tab broadcast (web only)
  useEffect(() => {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(BROADCAST_CHANNEL);
    channel.onmessage = (event) => {
      const { type, queryKey } = event.data || {};
      if (type === "invalidate" && Array.isArray(queryKey)) {
        qc.invalidateQueries({ queryKey });
      } else if (type === "invalidate-all") {
        invalidateAll(qc);
      }
    };

    return () => channel.close();
  }, [qc]);
}

/**
 * Broadcast a manual invalidation to every other tab on the same browser.
 * Useful right after a local mutation so siblings refresh immediately.
 */
export function broadcastSync(queryKey: string[]) {
  if (typeof BroadcastChannel === "undefined") return;
  const channel = new BroadcastChannel(BROADCAST_CHANNEL);
  channel.postMessage({ type: "invalidate", queryKey });
  channel.close();
}
